import { useState, useEffect, useRef } from 'react';
import { 
  FolderOpen, Heart, Briefcase, MessageSquare, FileText, Users, 
  Upload, Trash2, Plus, Loader2, X, Megaphone, CheckCircle, XCircle,
  Shield, Key, Eye, EyeOff, UserCog, Crown, UserMinus, AlertTriangle,
  Edit, Power, PowerOff, Globe, Mail, Phone, MapPin, Building2, Tag,
  Image, Link, Calendar, Filter, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

export const AdminDashboard = () => {
  const { user, api, logout, supabase } = useAuth();
  const [stats, setStats] = useState({ resources: 0, vacancies: 0, unread_suggestions: 0, members: 0, active_ads: 0, pending_ads: 0, pending_ad_inquiries: 0 });
  const [resources, setResources] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [welfare, setWelfare] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [adInquiries, setAdInquiries] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVacancyModal, setShowVacancyModal] = useState(false);
  const [showWelfareModal, setShowWelfareModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, resourcesData, vacanciesData, suggestionsData, welfareData, adsData, inquiriesData, usersData] = await Promise.all([
        api.getStats(),
        api.getResources(),
        api.getVacancies(),
        api.getSuggestions(),
        api.getWelfare(),
        api.getAllAdvertisements(), // ✅ Changed to get ALL ads for admin
        api.getAdInquiries(),
        api.getUsers(),
      ]);

      setStats(statsData || { resources: 0, vacancies: 0, unread_suggestions: 0, members: 0, active_ads: 0, pending_ads: 0, pending_ad_inquiries: 0 });
      setResources(resourcesData || []);
      setVacancies(vacanciesData || []);
      setSuggestions(suggestionsData || []);
      setWelfare(welfareData || []);
      setAdvertisements(adsData || []);
      setAdInquiries(inquiriesData || []);
      setAllUsers(usersData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    { icon: FileText, label: 'Total Resources', value: stats.resources, color: 'text-primary' },
    { icon: Briefcase, label: 'Active Vacancies', value: stats.vacancies, color: 'text-primary' },
    { icon: Megaphone, label: 'Active Ads', value: stats.active_ads, color: 'text-primary' },
    { icon: Users, label: 'Total Members', value: stats.members, color: 'text-primary' },
  ];

  return (
    <div className="min-h-screen pb-20">
      <div 
        className="relative py-8 px-4"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-primary text-xs font-bold uppercase tracking-widest">Dashboard</span>
              <h1 className="text-3xl font-heading font-bold text-foreground">Welcome, Admin</h1>
              <p className="text-muted-foreground text-sm">Association of Kenya IRE Teachers</p>
            </div>
            <Button 
              variant="destructive" 
              onClick={logout}
              className="bg-red-500/10 text-red-400 hover:bg-red-500/20"
              data-testid="admin-logout"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-8 mb-8">
          {statsCards.map((stat, index) => (
            <div 
              key={index}
              className="glass-card-heavy rounded-xl p-4 flex items-center gap-3"
              data-testid={`stat-card-${index}`}
            >
              <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="resources" className="w-full">
          <TabsList className="w-full justify-start bg-black/40 border border-white/10 mb-6 overflow-x-auto no-scrollbar">
            <TabsTrigger value="resources" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FolderOpen className="h-4 w-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="vacancies" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Briefcase className="h-4 w-4 mr-2" />
              Vacancies
            </TabsTrigger>
            <TabsTrigger value="welfare" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Heart className="h-4 w-4 mr-2" />
              Welfare
            </TabsTrigger>
            <TabsTrigger value="ads" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Megaphone className="h-4 w-4 mr-2" />
              Ads
              {(stats.pending_ad_inquiries > 0 || stats.pending_ads > 0) && (
                <span className="ml-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {stats.pending_ad_inquiries + (stats.pending_ads || 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="h-4 w-4 mr-2" />
              Suggestions
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Shield className="h-4 w-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <UserCog className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="resources">
            <ResourcesTab 
              resources={resources} 
              onRefresh={fetchData} 
              showUploadModal={showUploadModal}
              setShowUploadModal={setShowUploadModal}
              api={api}
              supabase={supabase}
            />
          </TabsContent>

          <TabsContent value="vacancies">
            <VacanciesTab 
              vacancies={vacancies} 
              onRefresh={fetchData}
              showModal={showVacancyModal}
              setShowModal={setShowVacancyModal}
              api={api}
            />
          </TabsContent>

          <TabsContent value="welfare">
            <WelfareTab 
              welfare={welfare} 
              onRefresh={fetchData}
              showModal={showWelfareModal}
              setShowModal={setShowWelfareModal}
              api={api}
            />
          </TabsContent>

          <TabsContent value="ads">
            <AdsTab 
              advertisements={advertisements}
              adInquiries={adInquiries}
              onRefresh={fetchData}
              api={api}
            />
          </TabsContent>

          <TabsContent value="suggestions">
            <SuggestionsTab 
              suggestions={suggestions} 
              onRefresh={fetchData}
              api={api}
            />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab 
              api={api}
              user={user}
              supabase={supabase}
            />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab 
              users={allUsers}
              currentUser={user}
              onRefresh={fetchData}
              api={api}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// =====================
// RESOURCES TAB
// =====================
const ResourcesTab = ({ resources, onRefresh, showUploadModal, setShowUploadModal, api, supabase }) => {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'exams',
    year: '',
    level: '',
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    setFileError('');
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError(`File too large! Maximum size is 2 MB. Your file: ${formatFileSize(selectedFile.size)}`);
      setFile(null);
      toast.error('File must be under 2 MB. Please compress your PDF.');
      e.target.value = '';
      return;
    }

    const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setFileError('Invalid file type! Only PDF, DOC, and DOCX files are allowed.');
      setFile(null);
      toast.error('Only PDF and Word documents are allowed.');
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
    toast.success(`File selected: ${selectedFile.name} (${formatFileSize(selectedFile.size)})`);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File must be under 2 MB. Please compress your PDF.');
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `resources/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('resources')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('resources')
        .getPublicUrl(filePath);

      await api.createResource({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        year: formData.year,
        level: formData.level,
        file_url: publicUrl,
        file_type: fileExt,
      });

      toast.success('Resource uploaded successfully!');
      handleCloseModal();
      onRefresh();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload resource');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      await api.deleteResource(id);
      toast.success('Resource deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setFile(null);
    setFileError('');
    setFormData({ title: '', description: '', category: 'exams', year: '', level: '' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold">Manage Resources</h2>
        <Button className="btn-primary" onClick={() => setShowUploadModal(true)} data-testid="add-resource-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      <div className="space-y-3">
        {resources.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-xl">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No resources uploaded yet</p>
          </div>
        ) : (
          resources.map((resource) => (
            <div key={resource.id} className="glass-card rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{resource.title}</p>
                  <p className="text-xs text-muted-foreground">{resource.category} • {resource.file_type?.toUpperCase()}</p>
                </div>
              </div>
              <Button 
                variant="destructive" 
                size="icon"
                onClick={() => handleDelete(resource.id)}
                data-testid={`delete-resource-${resource.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="glass-card-heavy rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold">Upload Resource</h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-yellow-400 font-medium">
                  Maximum file size: <strong>2 MB</strong>
                </p>
                <p className="text-xs text-yellow-400/70 mt-1">
                  Tip: Compress large PDFs at{' '}
                  <a 
                    href="https://www.ilovepdf.com/compress_pdf" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-yellow-300"
                  >
                    ilovepdf.com
                  </a>
                </p>
              </div>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-glass"
                  placeholder="KCSE Revision Papers 2024"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-glass w-full py-2 resize-none"
                  rows={3}
                  placeholder="Comprehensive revision papers for IRE"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value, level: '' })}
                    className="input-glass w-full h-10"
                  >
                    <option value="exams">Exams</option>
                    <option value="teaching_notes">Teaching Notes</option>
                    <option value="schemes_of_work">Schemes of Work</option>
                    <option value="cbe_curriculum">C.B.E Curriculum</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="input-glass w-full h-10"
                  >
                    <option value="">Select Level</option>
                    <optgroup label="Secondary (8-4-4)">
                      <option value="Form 1">Form 1</option>
                      <option value="Form 2">Form 2</option>
                      <option value="Form 3">Form 3</option>
                      <option value="Form 4">Form 4</option>
                    </optgroup>
                    <optgroup label="C.B.E Curriculum">
                      <option value="Grade 1">Grade 1</option>
                      <option value="Grade 2">Grade 2</option>
                      <option value="Grade 3">Grade 3</option>
                      <option value="Grade 4">Grade 4</option>
                      <option value="Grade 5">Grade 5</option>
                      <option value="Grade 6">Grade 6</option>
                      <option value="Grade 7">Grade 7</option>
                      <option value="Grade 8">Grade 8</option>
                      <option value="Grade 9">Grade 9</option>
                      <option value="Grade 10">Grade 10</option>
                      <option value="Grade 11">Grade 11</option>
                      <option value="Grade 12">Grade 12</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Year</label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="input-glass"
                  placeholder="2024"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">File (PDF or Word) — Max 2 MB</label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    fileError ? 'border-red-500/50 bg-red-500/5' : file ? 'border-green-500/50 bg-green-500/5' : 'border-primary/50 hover:border-primary'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className={`h-8 w-8 mx-auto mb-2 ${fileError ? 'text-red-400' : file ? 'text-green-400' : 'text-primary'}`} />
                  {file ? (
                    <div>
                      <p className="text-sm text-green-400 font-medium">{file.name}</p>
                      <p className="text-xs text-green-400/70 mt-1">Size: {formatFileSize(file.size)} ✓</p>
                    </div>
                  ) : fileError ? (
                    <p className="text-sm text-red-400">{fileError}</p>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground">Click to upload PDF or Word document</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Maximum size: 2 MB</p>
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={uploading || !file || !!fileError}>
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Upload Resource'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
// VACANCIES TAB
// =====================
const VacanciesTab = ({ vacancies, onRefresh, showModal, setShowModal, api }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    requirements: '',
    deadline: '',
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.createVacancy(formData);
      toast.success('Vacancy created successfully!');
      setShowModal(false);
      setFormData({ title: '', description: '', location: '', requirements: '', deadline: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create vacancy');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vacancy?')) return;

    try {
      await api.deleteVacancy(id);
      toast.success('Vacancy deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete vacancy');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold">Manage Vacancies</h2>
        <Button className="btn-primary" onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Post Vacancy
        </Button>
      </div>

      <div className="space-y-3">
        {vacancies.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-xl">
            <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No vacancies posted yet</p>
          </div>
        ) : (
          vacancies.map((vacancy) => (
            <div key={vacancy.id} className="glass-card rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{vacancy.title}</p>
                  <p className="text-xs text-muted-foreground">{vacancy.location}</p>
                </div>
              </div>
              <Button variant="destructive" size="icon" onClick={() => handleDelete(vacancy.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="glass-card-heavy rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold">Post Vacancy</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Job Title</label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="input-glass" placeholder="IRE Teacher" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Location</label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="input-glass" placeholder="Nairobi, Kenya" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-glass w-full py-2 resize-none" rows={3} placeholder="Job description..." required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Requirements</label>
                <textarea value={formData.requirements} onChange={(e) => setFormData({ ...formData, requirements: e.target.value })} className="input-glass w-full py-2 resize-none" rows={3} placeholder="Requirements..." required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Deadline</label>
                <Input value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} className="input-glass" placeholder="January 31, 2024" />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Post Vacancy'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
// WELFARE TAB
// =====================
const WelfareTab = ({ welfare, onRefresh, showModal, setShowModal, api }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', target_amount: '' });

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.createWelfare({
        ...formData,
        target_amount: formData.target_amount ? parseFloat(formData.target_amount) : null,
      });
      toast.success('Welfare need posted successfully!');
      setShowModal(false);
      setFormData({ title: '', description: '', target_amount: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to post welfare need');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this welfare need?')) return;

    try {
      await api.deleteWelfare(id);
      toast.success('Welfare need deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete welfare need');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold">Manage Welfare Needs</h2>
        <Button className="btn-primary" onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Post Need
        </Button>
      </div>

      <div className="space-y-3">
        {welfare.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-xl">
            <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No welfare needs posted yet</p>
          </div>
        ) : (
          welfare.map((item) => (
            <div key={item.id} className="glass-card rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{item.title}</p>
                  {item.target_amount && <p className="text-xs text-primary">Target: KES {item.target_amount.toLocaleString()}</p>}
                </div>
              </div>
              <Button variant="destructive" size="icon" onClick={() => handleDelete(item.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="glass-card-heavy rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold">Post Welfare Need</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Title</label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="input-glass" placeholder="Medical Support Needed" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-glass w-full py-2 resize-none" rows={3} placeholder="Details about the need..." required />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Target Amount (KES)</label>
                <Input type="number" value={formData.target_amount} onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })} className="input-glass" placeholder="50000" />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Post Welfare Need'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
// SUGGESTIONS TAB
// =====================
const SuggestionsTab = ({ suggestions, onRefresh, api }) => {
  const markAsRead = async (id) => {
    try {
      await api.markSuggestionRead(id);
      toast.success('Marked as read');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update suggestion');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div>
      <h2 className="text-xl font-heading font-bold mb-6">Teacher Suggestions</h2>

      <div className="space-y-3">
        {suggestions.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-xl">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No suggestions received yet</p>
          </div>
        ) : (
          suggestions.map((suggestion) => (
            <div key={suggestion.id} className={`glass-card rounded-xl p-5 ${!suggestion.is_read ? 'border-primary/50' : ''}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-foreground">{suggestion.name}</p>
                  <p className="text-xs text-muted-foreground">{suggestion.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{formatDate(suggestion.created_at)}</p>
                  {!suggestion.is_read && (
                    <span className="text-[10px] bg-primary text-primary-foreground px-2 py-0.5 rounded-full">New</span>
                  )}
                </div>
              </div>
              <p className="font-medium text-primary mb-2">{suggestion.subject}</p>
              <p className="text-sm text-muted-foreground mb-4">{suggestion.message}</p>
              {!suggestion.is_read && (
                <Button variant="outline" size="sm" onClick={() => markAsRead(suggestion.id)}>Mark as Read</Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
// =====================
// ✅ ENHANCED ADS TAB
// =====================
const AdsTab = ({ advertisements, adInquiries, onRefresh, api }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('ads');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const initialFormData = {
    title: '',
    description: '',
    business_name: '',
    category: 'other',
    image_url: '',
    website: '',
    email: '',
    phone: '',
    location: '',
    is_active: true,
    is_approved: true,
  };
  
  const [formData, setFormData] = useState(initialFormData);

  // Categories matching the Advertisements display page
  const categories = [
    { value: 'islamic_books', label: 'Islamic Books & Publishing' },
    { value: 'educational_materials', label: 'Educational Materials' },
    { value: 'halal_food', label: 'Halal Food & Beverages' },
    { value: 'islamic_clothing', label: 'Islamic Clothing & Attire' },
    { value: 'halal_finance', label: 'Halal Financial Services' },
    { value: 'travel_umrah', label: 'Travel & Umrah Services' },
    { value: 'technology', label: 'EdTech & Technology' },
    { value: 'health_wellness', label: 'Health & Wellness' },
    { value: 'events_training', label: 'Events & Training' },
    { value: 'other', label: 'Other Services' },
  ];

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const handleOpenModal = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setFormData({
        title: ad.title || '',
        description: ad.description || '',
        business_name: ad.business_name || '',
        category: ad.category || 'other',
        image_url: ad.image_url || '',
        website: ad.website || '',
        email: ad.email || '',
        phone: ad.phone || '',
        location: ad.location || '',
        is_active: ad.is_active ?? true,
        is_approved: ad.is_approved ?? true,
      });
    } else {
      setEditingAd(null);
      setFormData(initialFormData);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAd(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingAd) {
        await api.updateAdvertisement(editingAd.id, formData);
        toast.success('Advertisement updated successfully!');
      } else {
        await api.createAdvertisement(formData);
        toast.success('Advertisement created successfully!');
      }
      handleCloseModal();
      onRefresh();
    } catch (error) {
      console.error('Error saving advertisement:', error);
      toast.error(editingAd ? 'Failed to update advertisement' : 'Failed to create advertisement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;

    try {
      await api.deleteAdvertisement(id);
      toast.success('Advertisement deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete advertisement');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.toggleAdvertisement(id, !currentStatus);
      toast.success(`Advertisement ${!currentStatus ? 'activated' : 'deactivated'}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update advertisement status');
    }
  };

  const handleToggleApproval = async (id, currentStatus) => {
    try {
      if (currentStatus) {
        await api.rejectAdvertisement(id);
        toast.success('Advertisement rejected');
      } else {
        await api.approveAdvertisement(id);
        toast.success('Advertisement approved');
      }
      onRefresh();
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.updateInquiryStatus(id, status);
      toast.success(`Inquiry marked as ${status}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update inquiry status');
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;

    try {
      await api.deleteAdInquiry(id);
      toast.success('Inquiry deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'contacted': return 'bg-blue-500/20 text-blue-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // Filter advertisements
  const filteredAds = advertisements.filter(ad => {
    const matchesSearch = 
      ad.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesFilter = true;
    if (filterStatus === 'active') matchesFilter = ad.is_active && ad.is_approved;
    else if (filterStatus === 'inactive') matchesFilter = !ad.is_active;
    else if (filterStatus === 'pending') matchesFilter = !ad.is_approved;
    
    return matchesSearch && matchesFilter;
  });

  // Filter inquiries
  const filteredInquiries = adInquiries.filter(inquiry => {
    const matchesSearch = 
      inquiry.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inquiry.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inquiry.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  // Stats
  const activeAdsCount = advertisements.filter(ad => ad.is_active && ad.is_approved).length;
  const pendingAdsCount = advertisements.filter(ad => !ad.is_approved).length;
  const pendingInquiriesCount = adInquiries.filter(inq => inq.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-primary">{advertisements.length}</p>
          <p className="text-xs text-muted-foreground">Total Ads</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{activeAdsCount}</p>
          <p className="text-xs text-muted-foreground">Active</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{pendingAdsCount}</p>
          <p className="text-xs text-muted-foreground">Pending Approval</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{pendingInquiriesCount}</p>
          <p className="text-xs text-muted-foreground">New Inquiries</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={activeSubTab === 'ads' ? 'default' : 'outline'}
          onClick={() => { setActiveSubTab('ads'); setSearchQuery(''); }}
          className={activeSubTab === 'ads' ? 'btn-primary' : ''}
        >
          <Megaphone className="h-4 w-4 mr-2" />
          Advertisements ({advertisements.length})
        </Button>
        <Button
          variant={activeSubTab === 'inquiries' ? 'default' : 'outline'}
          onClick={() => { setActiveSubTab('inquiries'); setSearchQuery(''); }}
          className={activeSubTab === 'inquiries' ? 'btn-primary' : ''}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Inquiries ({adInquiries.length})
          {pendingInquiriesCount > 0 && (
            <span className="ml-2 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
              {pendingInquiriesCount}
            </span>
          )}
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder={activeSubTab === 'ads' ? "Search advertisements..." : "Search inquiries..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-glass pl-10"
          />
        </div>
        {activeSubTab === 'ads' && (
          <>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-glass h-10 px-4 rounded-xl"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending Approval</option>
            </select>
            <Button className="btn-primary" onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Ad
            </Button>
          </>
        )}
      </div>

      {/* Advertisements List */}
      {activeSubTab === 'ads' && (
        <div className="space-y-3">
          {filteredAds.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-xl">
              <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || filterStatus !== 'all' 
                  ? 'No advertisements match your search' 
                  : 'No advertisements yet'}
              </p>
              <Button className="btn-primary mt-4" onClick={() => handleOpenModal()}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Advertisement
              </Button>
            </div>
          ) : (
            filteredAds.map((ad) => (
              <div 
                key={ad.id} 
                className={`glass-card rounded-xl p-4 ${!ad.is_approved ? 'border-yellow-500/50' : ''}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* Image Preview */}
                    {ad.image_url ? (
                      <div 
                        className="h-16 w-16 rounded-xl bg-cover bg-center flex-shrink-0"
                        style={{ backgroundImage: `url(${ad.image_url})` }}
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-8 w-8 text-primary/50" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-semibold text-foreground">{ad.title}</p>
                        {/* Status Badges */}
                        {ad.is_approved && ad.is_active && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">
                            Active
                          </span>
                        )}
                        {ad.is_approved && !ad.is_active && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 font-medium">
                            Inactive
                          </span>
                        )}
                        {!ad.is_approved && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
                            Pending Approval
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-primary font-medium">{ad.business_name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{ad.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                        {ad.category && (
                          <span className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {getCategoryLabel(ad.category)}
                          </span>
                        )}
                        {ad.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {ad.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(ad.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {!ad.is_approved ? (
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleToggleApproval(ad.id, ad.is_approved)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleToggleActive(ad.id, ad.is_active)}
                      >
                        {ad.is_active ? (
                          <>
                            <PowerOff className="h-4 w-4 mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleOpenModal(ad)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(ad.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Contact Info Row */}
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                  {ad.email && (
                    <a href={`mailto:${ad.email}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                      <Mail className="h-3 w-3" />
                      {ad.email}
                    </a>
                  )}
                  {ad.phone && (
                    <a href={`tel:${ad.phone}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                      <Phone className="h-3 w-3" />
                      {ad.phone}
                    </a>
                  )}
                  {ad.website && (
                    <a href={ad.website.startsWith('http') ? ad.website : `https://${ad.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary transition-colors">
                      <Globe className="h-3 w-3" />
                      Website
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Inquiries List */}
      {activeSubTab === 'inquiries' && (
        <div className="space-y-3">
          {filteredInquiries.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-xl">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No inquiries match your search' : 'No advertising inquiries yet'}
              </p>
            </div>
          ) : (
            filteredInquiries.map((inquiry) => (
              <div 
                key={inquiry.id} 
                className={`glass-card rounded-xl p-5 ${inquiry.status === 'pending' ? 'border-primary/50' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-foreground">{inquiry.business_name}</p>
                    <p className="text-xs text-muted-foreground">{inquiry.contact_name} • {inquiry.email}</p>
                    {inquiry.phone && (
                      <p className="text-xs text-muted-foreground">Phone: {inquiry.phone}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${getStatusColor(inquiry.status)}`}>
                      {inquiry.status}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(inquiry.created_at)}</p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-sm text-primary font-medium">{inquiry.ad_type?.replace(/_/g, ' ').toUpperCase()}</p>
                  {inquiry.budget_range && (
                    <p className="text-xs text-muted-foreground">Budget: {inquiry.budget_range.replace(/_/g, ' - ')}</p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">{inquiry.message}</p>
                
                <div className="flex gap-2 flex-wrap">
                  {inquiry.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        className="bg-blue-500 hover:bg-blue-600"
                        onClick={() => updateInquiryStatus(inquiry.id, 'contacted')}
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Mark Contacted
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => updateInquiryStatus(inquiry.id, 'approved')}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => updateInquiryStatus(inquiry.id, 'rejected')}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {inquiry.status !== 'pending' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => updateInquiryStatus(inquiry.id, 'pending')}
                    >
                      Reset to Pending
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDeleteInquiry(inquiry.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Advertisement Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="glass-card-heavy rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold">
                {editingAd ? 'Edit Advertisement' : 'Create Advertisement'}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Business Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  Business Name *
                </label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  className="input-glass"
                  placeholder="Islamic Bookstore Kenya"
                  required
                />
              </div>

              {/* Ad Title */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Ad Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-glass"
                  placeholder="20% Discount for AKIT Members"
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-glass w-full h-10"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-glass w-full py-2 resize-none"
                  rows={3}
                  placeholder="Brief description of the business or offer..."
                  required
                />
              </div>

              {/* Image URL */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <Image className="h-3 w-3" />
                  Image URL
                </label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="input-glass"
                  placeholder="https://example.com/image.jpg"
                />
                {formData.image_url && (
                  <div className="mt-2 h-24 w-24 rounded-lg bg-cover bg-center border border-white/10" style={{ backgroundImage: `url(${formData.image_url})` }} />
                )}
              </div>

              {/* Website */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  Website URL
                </label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="input-glass"
                  placeholder="https://business-website.com"
                />
              </div>

              {/* Location */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Location
                </label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="input-glass"
                  placeholder="Nairobi, Kenya"
                />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-glass"
                    placeholder="contact@business.com"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-glass"
                    placeholder="+254 7XX XXX XXX"
                  />
                </div>
              </div>

              {/* Status Toggles (only show when editing) */}
              {editingAd && (
                <div className="flex gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_approved}
                      onChange={(e) => setFormData({ ...formData, is_approved: e.target.checked })}
                      className="w-4 h-4 rounded border-primary text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-muted-foreground">Approved</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-primary text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-muted-foreground">Active</span>
                  </label>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : editingAd ? (
                  'Update Advertisement'
                ) : (
                  'Create Advertisement'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
// SECURITY TAB
// =====================
const SecurityTab = ({ api, user, supabase }) => {
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: formData.new_password
      });

      if (error) throw error;

      toast.success('Password changed successfully!');
      setFormData({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold">Security Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your account security</p>
        </div>
      </div>

      <div className="glass-card-heavy rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-heading font-bold">Change Password</h3>
        </div>
        
        <p className="text-sm text-muted-foreground mb-6">
          Regularly changing your password helps protect your account from unauthorized access. 
          Use a strong password with at least 6 characters.
        </p>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">New Password</label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                className="input-glass pr-10"
                placeholder="Enter new password (min 6 characters)"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">Confirm New Password</label>
            <Input
              type={showNewPassword ? 'text' : 'password'}
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              className="input-glass"
              placeholder="Confirm your new password"
              required
            />
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 p-4 bg-primary/10 rounded-xl border border-primary/20">
          <p className="text-xs font-semibold text-primary mb-2">Security Tips:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Use a unique password not used on other sites</li>
            <li>• Include numbers, symbols, and mixed case letters</li>
            <li>• Change your password immediately if you suspect unauthorized access</li>
            <li>• Never share your password with anyone</li>
          </ul>
        </div>
      </div>

      <div className="glass-card rounded-xl p-4 mt-4">
        <p className="text-xs text-muted-foreground">
          Logged in as: <span className="text-primary font-medium">{user?.email}</span>
        </p>
      </div>
    </div>
  );
};

// =====================
// USERS TAB
// =====================
const UsersTab = ({ users, currentUser, onRefresh, api }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const handlePromote = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to make ${email} an admin?`)) return;

    try {
      await api.updateUserRole(userId, 'admin');
      toast.success(`${email} is now an admin!`);
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to promote user');
    }
  };

  const handleDemote = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to remove admin rights from ${email}?`)) return;

    try {
      await api.updateUserRole(userId, 'member');
      toast.success(`${email} is now a regular member`);
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to demote user');
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to DELETE ${email}? This action cannot be undone!`)) return;

    try {
      await api.deleteUser(userId);
      toast.success(`${email} has been deleted`);
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const adminCount = users.filter(u => u.role === 'admin').length;
  const memberCount = users.filter(u => u.role === 'member').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-bold">User Management</h2>
          <p className="text-sm text-muted-foreground">
            {adminCount} admins • {memberCount} members • {users.length} total
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-glass"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="input-glass h-10 px-4 rounded-xl"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admins Only</option>
          <option value="member">Members Only</option>
        </select>
      </div>

      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-xl">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No users found</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div 
              key={user.id} 
              className={`glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                user.role === 'admin' ? 'border-primary/30' : ''
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  user.role === 'admin' ? 'bg-primary/30' : 'bg-muted'
                }`}>
                  {user.role === 'admin' ? (
                    <Crown className="h-6 w-6 text-primary" />
                  ) : (
                    <Users className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-foreground">{user.full_name}</p>
                    {user.role === 'admin' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-bold uppercase">
                        Admin
                      </span>
                    )}
                    {user.id === currentUser?.id && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-bold">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <p className="text-xs text-muted-foreground">Joined: {formatDate(user.created_at)}</p>
                </div>
              </div>

              {user.id !== currentUser?.id && (
                <div className="flex items-center gap-2">
                  {user.role === 'member' ? (
                    <Button 
                      size="sm" 
                      className="bg-primary/20 text-primary hover:bg-primary/40"
                      onClick={() => handlePromote(user.id, user.email)}
                    >
                      <Crown className="h-4 w-4 mr-1" />
                      Make Admin
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDemote(user.id, user.email)}
                    >
                      <UserMinus className="h-4 w-4 mr-1" />
                      Remove Admin
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDelete(user.id, user.email)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-6 p-4 bg-primary/10 rounded-xl border border-primary/20">
        <p className="text-xs font-semibold text-primary mb-2">About Admin Roles:</p>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• <strong>Admins</strong> can manage resources, vacancies, ads, welfare, and other users</li>
          <li>• <strong>Members</strong> can view resources and submit suggestions</li>
          <li>• You cannot demote yourself or delete your own account</li>
          <li>• Deleted users cannot be recovered</li>
        </ul>
      </div>
    </div>
  );
};
